import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { demoEntryNote, getSolution, type SolutionId } from '../../data/solutionCatalog';
import { demoUrlFor } from '../../data/demoCenter';
import { fadeUp } from '../../utils/motion';
import { SolutionBody } from './SolutionBody';
type EmbeddedSolutionProps = {
  id: SolutionId;
  /** Stable section anchor, e.g. "scientific-research". */
  anchor: string;
  children?: React.ReactNode;
};

/** A complete solution presented as an expanded section of the Solutions page. */
export function EmbeddedSolution({
  id,
  anchor,
  children
}: EmbeddedSolutionProps) {
  const solution = getSolution(id);
  const titleId = `${anchor}-title`;
  return <div id={anchor} className="scroll-mt-20" data-id="element-2530" data-mp="9w80geb78kxa">
      <section aria-labelledby={titleId} className="bg-white border-y border-line py-16 lg:py-24" data-id="element-2531" data-mp="17ceuce44jkix">
        <motion.div {...fadeUp} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-2532" data-mp="12loq547fpnpt">
          <div className="lg:col-span-5" data-id="element-2533" data-mp="1vdqa2x5m4970">
            <p className="text-sm font-medium text-teal mb-4" data-id="element-2534" data-mp="1vg31s2169xx9">
              {solution.pageTitle} · {solution.offering}
            </p>
            <h2 id={titleId} className="font-heading font-extrabold text-4xl lg:text-5xl text-ink-dark leading-[1.08] tracking-tight" data-id="element-2535" data-mp="1mly6n6f0a0bx">
              {solution.headline}
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-8" data-id="element-2536" data-mp="1cmz4m6oqx2xw">
            {solution.intro.map((paragraph, index) => <p key={paragraph} className={index === 0 ? 'text-xl text-ink-dark leading-relaxed' : 'mt-4 text-lg text-ink-muted leading-relaxed'} data-id="element-2537" data-mp="1hmv9e859oyei" data-mpi={index}>
                {paragraph}
              </p>)}
            <a href={demoUrlFor(solution.id)} target="_blank" rel="noopener noreferrer" className="mt-8 inline-flex items-center justify-center gap-2 border border-navy text-navy hover:bg-surface-muted font-semibold px-5 py-3 rounded-md transition-colors duration-150 whitespace-nowrap" data-id="element-2538" data-mp="17qi74lvnpc85">
              {solution.demoLabel}
              <ArrowUpRight size={18} aria-hidden="true" data-id="element-2539" data-mp="9x0p2mzxtg7i" />
              <span className="sr-only" data-id="element-2540" data-mp="5uceoavks18f">(opens in a new tab)</span>
            </a>
            <p className="mt-3 text-sm text-ink-muted" data-id="element-2541" data-mp="urexy8kzoun">{demoEntryNote}</p>
          </div>
        </motion.div>
      </section>
      <SolutionBody solution={solution} nested data-id="element-2542" data-mp="xjfmg0mo7tpt">
        {children}
      </SolutionBody>
    </div>;
}